import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlineOutlined";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { toggleProfileVisibility } from "@/store/profile/profileActions";
import { railSectionSx, railSwitchSx } from "../styles";

const Visibility = () => {
  const { t } = useTranslation(["translation.Profile"]);
  const dispatch = useDispatch();
  const profileIsPublic = useSelector((state: any) =>
    Boolean(state.profile.isPublic)
  );

  const { register, watch, reset } = useForm({
    defaultValues: { is_public: profileIsPublic },
  });

  // keep the toggle in sync if the persisted value changes underneath us
  useEffect(() => {
    reset({ is_public: profileIsPublic });
  }, [profileIsPublic, reset]);

  const isPublic = watch("is_public");

  // MUI 9 dropped `inputRef`, and Switch's own props do not accept register()'s
  // validation attributes, so the whole registration goes to the input slot.
  const registration = register("is_public", {
    onChange: (e) => dispatch(toggleProfileVisibility(e.target.checked)),
  });

  const learnMore = (
    <Box>
      <Typography variant="subtitle2">{t("EditVisibility")}</Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        {t("NotLoggedinView")}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        <Box component="span" sx={{ fontWeight: "medium" }}>
          {t("on")}:
        </Box>{" "}
        {t("ifOn")}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        <Box component="span" sx={{ fontWeight: "medium" }}>
          {t("off")}:
        </Box>{" "}
        {t("ifOff")}
      </Typography>
    </Box>
  );

  return (
    <Box component="section" sx={railSectionSx}>
      <Stack spacing={0.5} sx={{ alignItems: "center" }}>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(isPublic)}
              slotProps={{ input: registration }}
              sx={railSwitchSx}
            />
          }
          label={isPublic ? t("on") : t("off")}
          sx={{ m: 0, color: "common.white" }}
        />
        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
          <Typography variant="body2" sx={{ color: "common.white" }}>
            {t("PublicVisibility")}
          </Typography>
          <Tooltip title={learnMore} arrow>
            <IconButton size="small" aria-label={t("EditVisibility")}>
              <HelpOutlineIcon fontSize="small" sx={{ color: "info.50" }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography
          variant="caption"
          sx={{ color: "grey.400", fontStyle: "italic" }}
        >
          {t("whoCanSee")}
        </Typography>
      </Stack>
    </Box>
  );
};

export default Visibility;
